import React, { useState, useEffect } from "react";
import {
  Shield,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Clock,
  Server,
  Globe,
} from "lucide-react";
import { changePassword, getHealthSummary } from "../api";
import { useAuth } from "../context/AuthContext";

export default function Admin() {
  const { user } = useAuth();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
        <p className="text-gray-500 text-sm mt-1">
          Logged in as <span className="text-brand-400 font-mono">{user?.username}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Change Password */}
        <ChangePasswordForm />

        {/* Uptime Summary */}
        <UptimeSummary />
      </div>
    </div>
  );
}

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', message }
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (newPassword !== confirmPassword) {
      setStatus({ type: "error", message: "New passwords don't match" });
      return;
    }

    if (newPassword.length < 8) {
      setStatus({
        type: "error",
        message: "Password must be at least 8 characters",
      });
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setStatus({ type: "success", message: "Password changed successfully!" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setStatus({
        type: "error",
        message: err.response?.data?.detail || "Failed to change password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-brand-600/20 flex items-center justify-center">
          <Lock className="w-5 h-5 text-brand-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">Change Password</h2>
          <p className="text-xs text-gray-500">Update your admin password</p>
        </div>
      </div>

      {status && (
        <div
          className={`flex items-center gap-2 text-sm rounded-lg px-4 py-3 mb-4 ${
            status.type === "success"
              ? "bg-green-500/10 border border-green-500/20 text-green-400"
              : "bg-red-500/10 border border-red-500/20 text-red-400"
          }`}
        >
          {status.type === "success" ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {status.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <PasswordInput
          label="Current Password"
          value={currentPassword}
          onChange={setCurrentPassword}
          show={showPasswords}
        />
        <PasswordInput
          label="New Password"
          value={newPassword}
          onChange={setNewPassword}
          show={showPasswords}
        />
        <PasswordInput
          label="Confirm New Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          show={showPasswords}
        />

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => setShowPasswords(!showPasswords)}
            className="text-gray-500 hover:text-gray-300 text-sm flex items-center gap-1 transition-colors"
          >
            {showPasswords ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            {showPasswords ? "Hide" : "Show"} passwords
          </button>

          <button
            type="submit"
            disabled={loading}
            className="bg-brand-600 hover:bg-brand-500 disabled:bg-brand-600/50 text-white font-medium py-2 px-6 rounded-lg text-sm transition-colors"
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PasswordInput({ label, value, onChange, show }) {
  return (
    <div>
      <label className="block text-sm text-gray-400 mb-1.5">{label}</label>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2.5 px-4 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm font-mono"
        required
      />
    </div>
  );
}

function UptimeSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getHealthSummary()
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">
            Uptime Summary (24h)
          </h2>
          <p className="text-xs text-gray-500">
            Availability metrics for all sites
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data?.summary ? (
        <div className="space-y-4">
          {data.summary.map((site) => (
            <div
              key={site.site_id}
              className="bg-gray-800/50 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">{site.name}</h4>
                <span className="text-xs text-gray-500">
                  {site.total_checks} checks
                </span>
              </div>

              {/* Frontend uptime bar */}
              <UptimeBar
                icon={Globe}
                label="Frontend"
                pct={site.frontend_uptime_pct}
                avgMs={site.avg_frontend_response_ms}
              />

              {/* Backend uptime bar */}
              <UptimeBar
                icon={Server}
                label="Backend"
                pct={site.backend_uptime_pct}
                avgMs={site.avg_backend_response_ms}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-sm text-center py-8">
          No uptime data available yet. Data will appear after health checks
          accumulate.
        </p>
      )}
    </div>
  );
}

function UptimeBar({ icon: Icon, label, pct, avgMs }) {
  const barColor =
    pct >= 99 ? "bg-green-500" : pct >= 95 ? "bg-yellow-500" : "bg-red-500";
  const textColor =
    pct >= 99
      ? "text-green-400"
      : pct >= 95
      ? "text-yellow-400"
      : "text-red-400";

  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-400 flex items-center gap-1">
          <Icon className="w-3 h-3" />
          {label}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 font-mono flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {avgMs.toFixed(0)}ms
          </span>
          <span className={`font-semibold font-mono ${textColor}`}>
            {pct.toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}
